import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { SupportChatService, SupportConversation, SupportChatMessage } from '@app-shared/services/support-chat.service';
import { SocketService } from '@app/socket.service';
import { UtilService } from '@app-shared/services/util.service';
import { AccountService } from '@app/account/account.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

export interface ExtendedChatMessage {
  id: string;
  comment_id?: number;
  user_id?: number;
  org_id?: number;
  text: string;
  author_name?: string;
  user_email?: string;
  phone?: string;
  org_name?: string;
  role?: string;
  sender_type: 'admin' | 'user';
  sender: 'incoming' | 'outgoing';
  created_at?: string;
  time?: string;
}

@Component({
  selector: 'app-chat-with-users',
  templateUrl: './chat-with-users.component.html',
  styleUrls: ['./chat-with-users.component.scss'],
  imports: [CommonModule, FormsModule, RouterModule]
})
export class ChatWithUsersComponent implements OnInit, OnDestroy {
  @ViewChild('chatScrollContainer') chatScrollContainer?: ElementRef;
  @ViewChild('messageTextarea') messageTextarea?: ElementRef;

  // Conversations & Chat State
  conversations: SupportConversation[] = [];
  selectedConversation: SupportConversation | null = null;
  messages: ExtendedChatMessage[] = [];

  // Loading States
  isLoadingConversations: boolean = false;
  isLoadingMessages: boolean = false;
  isSendingMessage: boolean = false;

  // Search & Filters
  searchQuery: string = '';
  activeFilter: 'all' | 'unread' | 'guest' | 'registered' = 'all';

  // UI Panels
  showUserDetailsPanel: boolean = true;
  isAutoRefreshEnabled: boolean = true;
  private autoRefreshTimer: any = null;

  // Send Invite Modal State
  showSendInviteModal: boolean = false;
  isSendingInvite: boolean = false;

  inviteForm = {
    email: '',
    message: 'Hello! You have been granted access as a Support Member to respond to user chats on Certiplate. Please click below to access the support inbox.'
  };
  inviteErrors = {
    email: '',
    message: ''
  };

  // Composer
  newMessageText: string = '';

  // Permission & Access State
  isCheckingAccess: boolean = true;
  hasAccess: boolean = false;
  accessDenialReason: 'not_logged_in' | 'email_mismatch' | 'org_mismatch' | 'not_invited' | 'error' = 'not_invited';
  accessDenialMessage: string = '';
  invitedEmailFromUrl: string = '';
  authorizedOrgs: Array<{ user_id: number; organization_id: number; org_name: string }> = [];

  private subs: Subscription = new Subscription();

  constructor(
    private supportChatService: SupportChatService,
    private socketService: SocketService,
    private util: UtilService,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.checkPageAccess();
  }

  checkPageAccess(): void {
    this.isCheckingAccess = true;
    this.invitedEmailFromUrl = (this.route.snapshot.queryParams['email'] || '').trim();

    if (!this.isLoggedIn) {
      this.isCheckingAccess = false;
      this.hasAccess = false;
      this.accessDenialReason = 'not_logged_in';
      this.accessDenialMessage = 'Please sign in to your Certiplate account to access Chat with Users.';
      this.cdr.detectChanges();
      return;
    }

    if (this.isSystemSuperAdmin) {
      this.isCheckingAccess = false;
      this.hasAccess = true;
      this.initPageData();
      return;
    }

    const currentEmail = (this.currentUserEmail || '').trim().toLowerCase();
    const currentOrgId = this.currentOrgId;
    const userId = this.currentUserId;

    if (this.invitedEmailFromUrl && currentEmail && this.invitedEmailFromUrl.toLowerCase() !== currentEmail) {
      this.isCheckingAccess = false;
      this.hasAccess = false;
      this.accessDenialReason = 'email_mismatch';
      this.accessDenialMessage = `This invitation was sent to ${this.invitedEmailFromUrl}, but you are currently signed in as ${currentEmail}.`;
      this.cdr.detectChanges();
      return;
    }

    const checkEmail = this.invitedEmailFromUrl || currentEmail;

    this.supportChatService.checkAccess(checkEmail, currentOrgId, userId).subscribe({
      next: (res) => {
        this.isCheckingAccess = false;
        if (res && res.hasAccess) {
          this.hasAccess = true;
          this.initPageData();
        } else {
          this.hasAccess = false;
          if (res?.reason === 'org_mismatch') {
            this.accessDenialReason = 'org_mismatch';
            this.authorizedOrgs = res.validOrgs || [];
            this.accessDenialMessage = res.message || `The email ${checkEmail} is authorized for Chat with Users, but does not belong to the currently selected organization (${this.currentOrgName}).`;
          } else {
            this.accessDenialReason = 'not_invited';
            this.accessDenialMessage = res?.message || `The email ${checkEmail} has not been invited or authorized to access Chat with Users.`;
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isCheckingAccess = false;
        this.hasAccess = false;
        this.accessDenialReason = 'error';
        this.accessDenialMessage = 'Failed to verify permission. Please check your connection and try again.';
        this.cdr.detectChanges();
      }
    });
  }

  initPageData(): void {
    this.loadConversations(true);
    this.setupSocketListener();
    this.startAutoRefresh();

    // Check query params if a specific user was requested
    this.subs.add(
      this.route.queryParams.subscribe(params => {
        const targetUserId = params['userId'] ? Number(params['userId']) : null;
        if (targetUserId && this.conversations.length > 0) {
          const match = this.conversations.find(c => Number(c.user_id) === targetUserId);
          if (match) {
            this.selectConversation(match);
          }
        }
      })
    );
  }

  goToDashboard(): void {
    this.router.navigate(['/Dashboard']);
  }

  goToLogin(): void {
    this.router.navigate(['/account/login']);
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.subs.unsubscribe();
  }

  // --------------------------------------------------------------------------
  // User & Organization Helpers
  // --------------------------------------------------------------------------
  get isLoggedIn(): boolean {
    const token = localStorage.getItem('JwtToken') || localStorage.getItem('token');
    const uid = localStorage.getItem('UserId') || localStorage.getItem('userId') || localStorage.getItem('id');
    return !!(token && uid);
  }

  get isSystemSuperAdmin(): boolean {
    if (!this.isLoggedIn) return false;
    const isSsa = localStorage.getItem('isSystemSuperAdmin') === 'true';
    const role = (localStorage.getItem('role') || '').toLowerCase();
    const ur = this.getUserRole();
    const orgId = this.currentOrgId;
    return isSsa || ur === 'SSA' || role === 'system super admin' || role === 'ssa' || (ur === 'SA' && orgId === -1);
  }

  get currentUserId(): number {
    const raw = localStorage.getItem('UserId') || localStorage.getItem('userId') || localStorage.getItem('id');
    return raw ? parseInt(raw, 10) : 1;
  }

  get currentUserName(): string {
    const candidateKeys = ['username', 'fullname', 'full_name', 'name', 'user_name'];
    for (const key of candidateKeys) {
      const val = localStorage.getItem(key);
      if (val && typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed && trimmed !== 'null' && trimmed !== 'undefined' && trimmed.toLowerCase() !== 'user') {
          return trimmed;
        }
      }
    }
    return 'Support Team';
  }

  get currentUserEmail(): string {
    return localStorage.getItem('useremail') || localStorage.getItem('email') || '';
  }

  get currentOrgId(): number {
    const rawEnc = localStorage.getItem('OrgId');
    if (rawEnc) {
      const dec = this.util.decrypt(rawEnc);
      const parsed = parseInt(dec, 10);
      if (!isNaN(parsed)) return parsed;
    }
    const plainOrg = localStorage.getItem('org_id');
    if (plainOrg) {
      const parsed = parseInt(plainOrg, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return 1;
  }

  get currentOrgName(): string {
    const storedName = localStorage.getItem('current_org_name');
    if (storedName && storedName.trim()) return storedName;
    return 'Certiplate';
  }

  private getUserRole(): string {
    const encRole = localStorage.getItem('ur');
    return encRole ? (this.util.decrypt(encRole) || 'Admin').toUpperCase() : 'Admin';
  }

  formatNameFromEmail(email: string): string {
    if (!email || typeof email !== 'string') return '';
    const cleanEmail = email.trim();
    const usernamePart = cleanEmail.split('@')[0];
    if (!usernamePart) return cleanEmail;
    const words = usernamePart.split(/[._-]+/).filter(w => w.length > 0);
    if (words.length > 0) {
      return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    return usernamePart;
  }

  // --------------------------------------------------------------------------
  // Data Fetching
  // --------------------------------------------------------------------------
  loadConversations(isInitial: boolean = false): void {
    if (isInitial) {
      this.isLoadingConversations = true;
    }

    this.supportChatService.getConversations().subscribe({
      next: (conversations) => {
        if (Array.isArray(conversations)) {
          const seen = new Set<number>();
          const filtered = conversations.filter(c => {
            const uId = Number(c?.user_id);
            if (!uId || seen.has(uId)) return false;
            seen.add(uId);
            return true;
          }).map(c => {
            let cleanName = c.user_name || '';
            const lower = cleanName.trim().toLowerCase();
            if (!cleanName || lower === 'user' || lower === 'null' || lower === 'undefined' || lower === 'anonymous' || lower === 'anonymous user') {
              if (c.user_email && c.user_email.trim() && c.user_email !== '—') {
                cleanName = this.formatNameFromEmail(c.user_email.trim());
              } else if (c.user_id && Number(c.user_id) < 9000000) {
                cleanName = `User ${c.user_id}`;
              } else {
                cleanName = 'Anonymous User';
              }
            }
            return {
              ...c,
              user_name: cleanName
            };
          });

          this.conversations = filtered.sort((a, b) => {
            const tA = this.getConversationTimestamp(a);
            const tB = this.getConversationTimestamp(b);
            return tB - tA;
          });

          // If a conversation was selected, preserve selection
          if (this.selectedConversation) {
            const updated = this.conversations.find(c => c.user_id === this.selectedConversation?.user_id);
            if (updated) {
              this.selectedConversation = { ...updated };
            }
          } else if (isInitial && this.conversations.length > 0) {
            // Check route params if a specific user was directly linked
            const targetUserId = this.route.snapshot.queryParams['userId']
              ? Number(this.route.snapshot.queryParams['userId'])
              : null;
            if (targetUserId) {
              const match = this.conversations.find(c => Number(c.user_id) === targetUserId);
              if (match) {
                this.selectConversation(match);
              }
            }
          }
        } else {
          this.conversations = [];
        }
        this.isLoadingConversations = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error in loadConversations:', err);
        this.isLoadingConversations = false;
        this.cdr.detectChanges();
      }
    });
  }

  backToConversationsList(): void {
    this.selectedConversation = null;
    this.messages = [];
    this.cdr.detectChanges();
  }

  selectConversation(conv: SupportConversation): void {
    if (!conv) return;
    this.selectedConversation = conv;
    if (conv.has_unread) {
      conv.has_unread = false;
      this.supportChatService.markConversationAsRead(conv.user_id, conv.org_id).subscribe();
    }
    this.loadMessages(conv.user_id, conv.org_id);
  }

  toggleMarkUnread(conv: SupportConversation, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!conv) return;

    conv.has_unread = !conv.has_unread;
    if (conv.has_unread) {
      this.supportChatService.markConversationAsUnread(conv.user_id, conv.org_id).subscribe();
      this.toastr.info(`Chat with ${conv.user_name} marked as unread.`, 'Marked Unread');
    } else {
      this.supportChatService.markConversationAsRead(conv.user_id, conv.org_id).subscribe();
      this.toastr.success(`Chat with ${conv.user_name} marked as read.`, 'Marked Read');
    }
    this.cdr.detectChanges();
  }

  toggleCurrentChatUnread(): void {
    if (!this.selectedConversation) return;
    this.toggleMarkUnread(this.selectedConversation);
  }

  deleteMessage(msg: ExtendedChatMessage, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isSystemSuperAdmin) {
      this.toastr.error('Only System Super Admin can delete messages.', 'Access Denied');
      return;
    }

    if (!confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    const commentId = msg.comment_id || msg.id;
    const userId = msg.user_id || this.selectedConversation?.user_id;
    const orgId = msg.org_id || this.selectedConversation?.org_id || 0;

    if (!commentId || !userId) {
      this.toastr.error('Invalid message or user ID.', 'Error');
      return;
    }

    this.supportChatService.deleteMessage(commentId, userId, orgId).subscribe({
      next: (res) => {
        if (res && res.success !== false) {
          this.messages = this.messages.filter(m => String(m.id) !== String(msg.id) && String(m.comment_id) !== String(commentId));
          this.toastr.success('Message deleted successfully.', 'Deleted');
          this.cdr.detectChanges();
        } else {
          this.toastr.error(res?.error || 'Failed to delete message.', 'Error');
        }
      },
      error: (err) => {
        console.error('Delete message error:', err);
        this.toastr.error('Failed to delete message. Please try again.', 'Error');
      }
    });
  }

  isIncomingMessage(m: SupportChatMessage): boolean {
    if (!m) return false;

    // If client-side message explicitly marked as outgoing
    if ((m as any).sender === 'outgoing') return false;

    const senderType = (m.sender_type || '').trim().toLowerCase();
    const authorName = (m.author_name || '').trim().toLowerCase();
    const userEmail = (m.user_email || '').trim().toLowerCase();
    const userPhone = (m.phone || '').trim();
    const orgName = (m.org_name || '').trim().toLowerCase();

    const currentAdminName = (this.currentUserName || '').trim().toLowerCase();
    const currentAdminEmail = (this.currentUserEmail || '').trim().toLowerCase();

    const selectedCustName = (this.selectedConversation?.user_name || '').trim().toLowerCase();
    const selectedCustEmail = (this.selectedConversation?.user_email || '').trim().toLowerCase();
    const selectedCustPhone = (this.selectedConversation?.phone || this.selectedConversation?.user_phone || '').trim();

    // 1. Definite signals of a user / customer message
    if (senderType === 'user' || senderType === 'user_msg' || senderType === 'guest' || senderType === 'candidate') {
      return true;
    }

    // 2. If author matches the selected customer's name, email, or phone
    if (selectedCustName && authorName && (authorName === selectedCustName || authorName.startsWith(selectedCustName) || selectedCustName.startsWith(authorName))) {
      return true;
    }
    if (selectedCustEmail && userEmail && selectedCustEmail === userEmail && selectedCustEmail !== '—') {
      return true;
    }
    if (selectedCustPhone && userPhone && selectedCustPhone === userPhone && selectedCustPhone !== '—') {
      return true;
    }

    // 3. If author is explicitly Support Team, System Super Admin, or matches the current admin replying
    const isSupportAgent =
      authorName === 'support team' ||
      authorName === 'system super admin' ||
      authorName === 'support admin' ||
      authorName === 'admin support' ||
      (currentAdminName && authorName === currentAdminName) ||
      (currentAdminEmail && userEmail === currentAdminEmail);

    if (isSupportAgent) {
      return false; // Outgoing from Support Team
    }

    // 4. If author is from a customer organization (not the current support organization)
    const adminOrg = (this.currentOrgName || '').trim().toLowerCase();
    if (orgName && adminOrg && orgName !== adminOrg && !orgName.includes('support')) {
      return true; // Incoming from customer org
    }

    // 5. If sender_type is admin and not matched by any customer signal:
    if (senderType === 'admin' || senderType === 'admin_msg') {
      return false;
    }

    // Default: any message in this conversation not sent by the support team is an incoming message
    return true;
  }

  mapServerMessage(m: SupportChatMessage): ExtendedChatMessage {
    const isIncoming = this.isIncomingMessage(m);
    const authorName = isIncoming
      ? (m.author_name || this.selectedConversation?.user_name || 'User')
      : (m.author_name || 'Support Team');

    return {
      id: m.id,
      user_id: m.user_id,
      org_id: m.org_id,
      text: m.text,
      author_name: authorName,
      user_email: m.user_email,
      phone: m.phone,
      org_name: m.org_name,
      role: m.role,
      sender_type: m.sender_type,
      sender: isIncoming ? 'incoming' : 'outgoing',
      created_at: m.created_at,
      time: m.time || (m.created_at ? this.formatTimeOnly(m.created_at) : '')
    };
  }

  loadMessages(userId: number, orgId: number): void {
    this.isLoadingMessages = true;
    this.cdr.detectChanges();

    this.supportChatService.getMessages(userId, orgId).subscribe({
      next: (data: SupportChatMessage[]) => {
        if (data && Array.isArray(data)) {
          this.messages = data.map(m => this.mapServerMessage(m));
        } else {
          this.messages = [];
        }
        this.isLoadingMessages = false;
        this.scrollToBottom();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching messages for user:', userId, err);
        this.isLoadingMessages = false;
        this.cdr.detectChanges();
      }
    });
  }

  // --------------------------------------------------------------------------
  // Send Reply
  // --------------------------------------------------------------------------
  sendMessage(): void {
    const text = (this.newMessageText || '').trim();
    if (!text || !this.selectedConversation || this.isSendingMessage) return;

    this.isSendingMessage = true;
    const now = new Date();
    const nowIso = now.toISOString();
    const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    const targetUserId = this.selectedConversation.user_id;
    const targetOrgId = this.selectedConversation.org_id || 0;
    const senderName = this.currentUserName || 'Support Team';
    const senderRole = this.getUserRole() || 'Admin';
    const senderOrg = this.currentOrgName || 'Certiplate';

    // Optimistic message creation
    const tempMsg: ExtendedChatMessage = {
      id: String(Date.now()),
      user_id: targetUserId,
      org_id: targetOrgId,
      text: text,
      author_name: senderName,
      sender_type: 'admin',
      sender: 'outgoing',
      created_at: nowIso,
      time: timeStr
    };
    this.messages.push(tempMsg);
    this.newMessageText = '';

    // Update conversation preview snippet
    this.selectedConversation.last_message = text;
    this.selectedConversation.last_message_time = nowIso;
    (this.selectedConversation as any).last_message_iso = nowIso;

    // Move this conversation immediately to the TOP of the conversations list
    const convIdx = this.conversations.findIndex(c => c.user_id === targetUserId);
    if (convIdx > 0) {
      const [c] = this.conversations.splice(convIdx, 1);
      this.conversations.unshift(c);
    }

    this.scrollToBottom();
    this.cdr.detectChanges();

    this.supportChatService.sendMessage({
      user_id: targetUserId,
      organization_id: targetOrgId,
      user_name: senderName,
      role: senderRole,
      org_name: senderOrg,
      message_text: text,
      is_admin: true
    }).subscribe({
      next: (res) => {
        if (res && res.id) {
          tempMsg.id = String(res.id);
        }
        this.isSendingMessage = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to send admin reply:', err);
        this.toastr.error('Failed to send reply. Please try again.', 'Error');
        this.isSendingMessage = false;
        this.cdr.detectChanges();
      }
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // --------------------------------------------------------------------------
  // Auto-refresh & Real-time Sockets
  // --------------------------------------------------------------------------
  setupSocketListener(): void {
    this.subs.add(
      this.socketService.onNewNotification().subscribe((data: any) => {
        if (data && (data.title?.includes('Support') || data.message?.includes('support') || data.message?.includes('Support') || data.service_type === 'IN APP PUSH')) {
          this.silentRefreshConversations();
          if (this.selectedConversation) {
            this.silentRefreshMessages(this.selectedConversation.user_id, this.selectedConversation.org_id);
          }
        }
      })
    );
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    if (!this.isAutoRefreshEnabled) return;

    this.autoRefreshTimer = setInterval(() => {
      this.silentRefreshConversations();
      if (this.selectedConversation) {
        this.silentRefreshMessages(this.selectedConversation.user_id, this.selectedConversation.org_id);
      }
    }, 4000);
  }

  stopAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  // --------------------------------------------------------------------------
  // Send Invite Actions
  // --------------------------------------------------------------------------
  openSendInviteModal(conv?: SupportConversation): void {
    this.inviteErrors = { email: '', message: '' };
    this.inviteForm = {
      email: conv?.user_email && conv.user_email !== '—' ? conv.user_email : '',
      message: 'Hello! You have been granted access as a Support Member to respond to user chats on Certiplate. Please click below to access the support inbox.'
    };
    this.showSendInviteModal = true;
    this.cdr.detectChanges();
  }

  closeSendInviteModal(): void {
    this.showSendInviteModal = false;
    this.inviteErrors = { email: '', message: '' };
    this.cdr.detectChanges();
  }

  submitSendInvite(): void {
    this.inviteErrors = { email: '', message: '' };
    const email = (this.inviteForm.email || '').trim();
    const msg = (this.inviteForm.message || '').trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      this.inviteErrors.email = 'Email address is required.';
    } else if (!emailRegex.test(email)) {
      this.inviteErrors.email = 'Please enter a valid email address.';
    }

    if (!msg) {
      this.inviteErrors.message = 'Invitation message is required.';
    }

    if (this.inviteErrors.email || this.inviteErrors.message) {
      this.cdr.detectChanges();
      return;
    }

    this.isSendingInvite = true;

    this.supportChatService.sendInvite({
      email: email,
      message: msg,
      admin_name: this.currentUserName || 'Support Team',
      admin_email: this.currentUserEmail,
      organization_id: this.currentOrgId,
      org_name: this.currentOrgName || 'Certiplate',
      app_url: typeof window !== 'undefined' && window.location ? window.location.origin : ''
      }).subscribe({
      next: (res: any) => {
        this.isSendingInvite = false;
        this.toastr.success(`Support member invitation sent successfully to ${email}!`, 'Invitation Sent');
        this.closeSendInviteModal();
        this.selectedConversation = null;
        this.messages = [];
        this.loadConversations(false);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.warn('sendInvite endpoint error, trying fallback:', err);
        // Fallback to sendMessage
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
          hash = (hash << 5) - hash + email.charCodeAt(i);
          hash |= 0;
        }
        const fallbackUserId = 7000000 + Math.abs(hash % 900000);

        this.supportChatService.sendMessage({
          user_id: fallbackUserId,
          organization_id: this.currentOrgId,
          user_name: this.currentUserName || 'Support Team',
          user_email: email,
          role: this.getUserRole() || 'Admin',
          org_name: this.currentOrgName || 'Certiplate',
          message_text: msg,
          is_admin: true
        }).subscribe({
          next: () => {
            this.isSendingInvite = false;
            this.toastr.success(`Chat invite sent successfully to ${email}!`, 'Invite Sent');
            this.closeSendInviteModal();
            this.selectedConversation = null;
            this.messages = [];
            this.loadConversations(false);
            this.cdr.detectChanges();
          },
          error: (fErr: any) => {
            console.error('Failed to send invite:', fErr);
            this.isSendingInvite = false;
            this.toastr.error('Failed to send chat invite. Please try again.', 'Error');
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  silentRefreshConversations(): void {
    if (this.isLoadingConversations) return;

    this.supportChatService.getConversations().subscribe({
      next: (conversations) => {
        if (Array.isArray(conversations)) {
          const seen = new Set<number>();
          const filtered = conversations.filter(c => {
            const uId = Number(c?.user_id);
            if (!uId || seen.has(uId)) return false;
            seen.add(uId);
            return true;
          }).map(c => {
            let cleanName = c.user_name || '';
            const lower = cleanName.trim().toLowerCase();
            if (!cleanName || lower === 'user' || lower === 'null' || lower === 'undefined' || lower === 'anonymous' || lower === 'anonymous user') {
              if (c.user_email && c.user_email.trim() && c.user_email !== '—') {
                cleanName = this.formatNameFromEmail(c.user_email.trim());
              } else if (c.user_id && Number(c.user_id) < 9000000) {
                cleanName = `User ${c.user_id}`;
              } else {
                cleanName = 'Anonymous User';
              }
            }
            return {
              ...c,
              user_name: cleanName
            };
          });

          this.conversations = filtered;
          if (this.selectedConversation) {
            const updated = this.conversations.find(c => c.user_id === this.selectedConversation?.user_id);
            if (updated) {
              this.selectedConversation = { ...updated };
            }
          }
          this.cdr.detectChanges();
        }
      },
      error: () => {}
    });
  }

  silentRefreshMessages(userId: number, orgId: number): void {
    if (this.isLoadingMessages) return;

    this.supportChatService.getMessages(userId, orgId).subscribe({
      next: (data: SupportChatMessage[]) => {
        if (data && Array.isArray(data)) {
          const formatted: ExtendedChatMessage[] = data.map(m => this.mapServerMessage(m));

          const countChanged = formatted.length !== this.messages.length;
          const lastChanged = formatted.length > 0 && this.messages.length > 0 &&
            (formatted[formatted.length - 1]?.text !== this.messages[this.messages.length - 1]?.text ||
             formatted[formatted.length - 1]?.id !== this.messages[this.messages.length - 1]?.id);

          if (countChanged || lastChanged) {
            this.messages = formatted;
            this.scrollToBottom();
            this.cdr.detectChanges();
          }
        }
      },
      error: () => {}
    });
  }

  // --------------------------------------------------------------------------
  // Filters & Search
  // --------------------------------------------------------------------------
  setFilter(filter: 'all' | 'unread' | 'guest' | 'registered'): void {
    this.activeFilter = filter;
    this.cdr.detectChanges();
  }

  get filteredConversations(): SupportConversation[] {
    let list = this.conversations || [];

    // Filter by category
    if (this.activeFilter === 'unread') {
      list = list.filter(c => c.has_unread);
    } else if (this.activeFilter === 'guest') {
      list = list.filter(c => c.role === 'Guest' || c.org_id === 0 || Number(c.user_id) >= 9000000);
    } else if (this.activeFilter === 'registered') {
      list = list.filter(c => c.role !== 'Guest' && c.org_id !== 0 && Number(c.user_id) < 9000000);
    }

    // Filter by search query
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter(c => {
        const name = (c.user_name || '').toLowerCase();
        const email = (c.user_email || '').toLowerCase();
        const org = (c.org_name || '').toLowerCase();
        const lastMsg = (c.last_message || '').toLowerCase();
        const uId = String(c.user_id || '');
        return name.includes(q) || email.includes(q) || org.includes(q) || lastMsg.includes(q) || uId.includes(q);
      });
    }

    // Sort so newly received messages / chats with recent activity appear at the top
    return list.slice().sort((a, b) => {
      const tA = this.getConversationTimestamp(a);
      const tB = this.getConversationTimestamp(b);
      return tB - tA;
    });
  }

  getConversationTimestamp(c: SupportConversation): number {
    if ((c as any).last_message_iso) {
      const ms = new Date((c as any).last_message_iso).getTime();
      if (!isNaN(ms)) return ms;
    }
    if (c.last_message_time) {
      const ms = new Date(c.last_message_time).getTime();
      if (!isNaN(ms)) return ms;
    }
    return 0;
  }

  get unreadCount(): number {
    return (this.conversations || []).filter(c => c.has_unread).length;
  }

  get guestCount(): number {
    return (this.conversations || []).filter(c => c.role === 'Guest' || c.org_id === 0 || Number(c.user_id) >= 9000000).length;
  }

  get registeredCount(): number {
    return (this.conversations || []).filter(c => c.role !== 'Guest' && c.org_id !== 0 && Number(c.user_id) < 9000000).length;
  }

  // --------------------------------------------------------------------------
  // Utility & UI Helpers
  // --------------------------------------------------------------------------
  toggleUserDetails(): void {
    this.showUserDetailsPanel = !this.showUserDetailsPanel;
    this.cdr.detectChanges();
  }

  copyToClipboard(text: string, label: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.toastr.success(`${label} copied to clipboard!`, 'Copied');
    }).catch(() => {
      this.toastr.info(text, 'Copied');
    });
  }

  getInitials(name: string): string {
    if (!name || typeof name !== 'string') return 'U';
    const clean = name.trim();
    const parts = clean.split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
  }

  getAvatarColor(nameOrId: string | number): string {
    const colors = [
      '#059669', // Emerald
      '#2563eb', // Blue
      '#7c3aed', // Purple
      '#db2777', // Pink
      '#ea580c', // Orange
      '#0891b2', // Cyan
      '#4f46e5', // Indigo
      '#16a34a', // Green
      '#d97706'  // Amber
    ];
    const str = String(nameOrId || 'User');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % colors.length;
    return colors[idx];
  }

  formatTime(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch (_) {
      return '';
    }
  }

  formatTimeOnly(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) {
      return '';
    }
  }

  formatDateHeader(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      if (date.toDateString() === now.toDateString()) return 'Today';
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) {
      return '';
    }
  }

  isFirstMessageOfDay(index: number): boolean {
    if (index === 0) return true;
    const current = this.messages[index]?.created_at;
    const prev = this.messages[index - 1]?.created_at;
    if (!current || !prev) return false;
    return new Date(current).toDateString() !== new Date(prev).toDateString();
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatScrollContainer?.nativeElement) {
        const el = this.chatScrollContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 60);
  }

  trackByConv(index: number, conv: SupportConversation): number {
    return conv.user_id;
  }

  trackByMsg(index: number, msg: ExtendedChatMessage): string {
    return msg.id || String(index);
  }
}
